// Permissions Hook
import { useMemo } from 'react';
import useAuthStore from '../stores/authStore';

const usePermissions = () => {
  const user = useAuthStore((state) => state.user);
  const permissions = user?.backoffice_permissions || {};

  const hasPermission = useMemo(
    () => (module, action = 'read') => {
      if (!module) return false;

      const modulePermissions = permissions[module];
      if (!modulePermissions || !Array.isArray(modulePermissions)) {
        return false;
      }

      if (Array.isArray(action)) {
        return action.every((act) => modulePermissions.includes(act));
      }

      return modulePermissions.includes(action);
    },
    [permissions]
  );

  const hasAnyPermission = useMemo(
    () => (module, actions = []) => {
      if (!module || !Array.isArray(actions)) return false;

      const modulePermissions = permissions[module];
      // Vérifier que le tableau existe, n'est pas vide ET est un array
      if (!modulePermissions || !Array.isArray(modulePermissions) || modulePermissions.length === 0) {
        return false;
      }

      return actions.some((action) => modulePermissions.includes(action));
    },
    [permissions]
  );

  const canCreate = useMemo(() => (module) => hasPermission(module, 'create'), [hasPermission]);
  const canRead = useMemo(() => (module) => hasPermission(module, 'read'), [hasPermission]);
  const canUpdate = useMemo(() => (module) => hasPermission(module, 'update'), [hasPermission]);
  const canDelete = useMemo(() => (module) => hasPermission(module, 'delete'), [hasPermission]);

  const hasFullAccess = useMemo(
    () => (module) => hasPermission(module, ['create', 'read', 'update', 'delete']),
    [hasPermission]
  );

  const getAccessibleModules = useMemo(() => {
    return Object.keys(permissions).filter((module) => {
      const modulePerms = permissions[module];
      return Array.isArray(modulePerms) && modulePerms.length > 0;
    });
  }, [permissions]);

  const isAdmin = useMemo(() => {
    const allModules = Object.keys(permissions);
    if (allModules.length === 0) return false;

    return allModules.every((module) => {
      const modulePerms = permissions[module];
      return (
        Array.isArray(modulePerms) &&
        ['create', 'read', 'update', 'delete'].every(act => modulePerms.includes(act))
      );
    });
  }, [permissions]);

  const getUserRole = useMemo(() => {
    return user?.backoffice_role || user?.profil?.label || 'user';
  }, [user]);

  return {
    permissions,
    user,
    userRole: getUserRole,
    accessibleModules: getAccessibleModules,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    hasFullAccess,
  };
};

export default usePermissions;

